import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { ConselhoDtoUpdate, ConselhoList } from '../../core/_models'
import { conselhoSchema } from './validation/conselhoSchema'
import { createConselho, deleteConselho, updateConselho } from '../../core/_requests'
import {
  showAlertLoading,
  showAlert,
  showError,
  showConfirmAlert,
  IconPaths,
  IconSize,
  ButtonType,
} from '_metronic/helpers'
import { Button, ButtonIcon, FetchState, FormInput, useModal } from 'app/components'

export const ConselhosModal = () => {
  const { fetchStates, refetch } = useModal()

  const { data: conselhos = [] } = (fetchStates['conselhos'] || {}) as FetchState<ConselhoList[]>

  const [selected, setSelected] = useState<ConselhoList | null>(null)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { nome: '', sigla: '' },
    validationSchema: conselhoSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true)
      try {
        showAlertLoading()
        if (selected?.id) {
          const updated: ConselhoDtoUpdate = {
            id: selected.id,
            nome: values.nome,
            sigla: values.sigla,
          }
          await updateConselho(updated)
        } else {
          await createConselho(values)
        }

        showAlert({
          title: 'Sucesso',
          text: `Conselho ${selected ? 'atualizado' : 'criado'} com sucesso!`,
          icon: 'success',
        })

        refetch?.('conselhos')
        setSelected(null)
        resetForm()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleDelete = async (id: number) => {
    const isConfirmed = await showConfirmAlert()
    if (isConfirmed) {
      try {
        showAlertLoading()
        await deleteConselho(id)
        showAlert({
          title: 'Sucesso',
          text: `Tipo de unidade excluído com sucesso!`,
          icon: 'success',
        })
        refetch?.('conselhos')
      } catch (error) {
        showError(error)
      }
    }
  }

  const { setFieldValue } = formik

  useEffect(() => {
    if (selected) {
      setFieldValue('nome', selected.nome)
      setFieldValue('sigla', selected.sigla)
    }
  }, [selected, setFieldValue])

  return (
    <>
      <table className='table table-row-dashed'>
        <thead>
          <tr>
            <th className='fw-bold'>Sigla</th>
            <th className='fw-bold'>Nome</th>
            <th className='fw-bold text-end'>Ações</th>
          </tr>
        </thead>

        <tbody>
          {conselhos.map((conselho) => (
            <tr key={conselho.id}>
              <td className='align-middle'>{conselho.sigla}</td>
              <td className='align-middle'>{conselho.nome}</td>

              <td className='d-flex justify-content-end'>
                <ButtonIcon
                  className='p-0 me-2'
                  icon={IconPaths.Pen}
                  disabled={formik.isSubmitting}
                  onClick={() => setSelected(conselho)}
                  iconSize={IconSize.Small}
                />
                <ButtonIcon
                  className='m-0 p-0'
                  icon={IconPaths.Trash}
                  disabled={formik.isSubmitting}
                  onClick={() => handleDelete(conselho.id)}
                  iconSize={IconSize.Small}
                />
              </td>
            </tr>
          ))}

          {conselhos.length === 0 && (
            <tr>
              <td colSpan={2} className='text-center text-muted'>
                Nenhum conselho cadastrado
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <form noValidate onSubmit={formik.handleSubmit}>
        <div className='row mb-3'>
          <div className='col-md-3'>
            <FormInput
              formik={formik}
              label='Sigla'
              name='sigla'
              placeholder='Digite a sigla'
              className='mb-3'
              isDisabled={formik.isSubmitting}
              isRequired
            />
          </div>
          <div className='col-md-9'>
            <FormInput
              formik={formik}
              label='Nome'
              name='nome'
              placeholder='Digite o nome do conselho'
              className='mb-3'
              isDisabled={formik.isSubmitting}
              isRequired
            />
          </div>
        </div>

        <div className='d-flex flex-end'>
          <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
            Salvar
          </Button>
        </div>
      </form>
    </>
  )
}
